(() => {
  'use strict';
  const RATE_FALLBACK = 1 / 16.65;
  const MODE_KEY = 'orynta_display_currency_v2';
  const RATE_KEY = 'orynta_zar_usd_rate_v3';
  const RATE_TIME_KEY = 'orynta_zar_usd_rate_time_v3';
  const originals = new WeakMap();
  let rate = RATE_FALLBACK;
  let mode = 'USD';
  let observer;
  let applying = false;

  const priceSelector = [
    '.price', '.price-item', '.money', '[class*="price"]',
    '[data-price]', '[data-money]', '[data-product-price]',
    'product-card', '.product-card', '[data-product-card]',
    '.card-information', '.orynta-card-meta', '.ocp-info', '.cc3-info',
    '.orp-price', '.orp-add', 'cart-drawer', 'cart-items'
  ].join(',');

  function parseZar(raw) {
    const cleaned = raw.replace(/\s/g, '').replace(/,/g, '');
    const value = Number(cleaned);
    return Number.isFinite(value) ? value : null;
  }

  function formatUsd(value) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(value * rate) + ' USD';
  }

  function convertTextNode(node) {
    if (node.parentElement?.closest('#orynta-currency-bar, .orynta-estimator, script, style')) return;
    const text = node.nodeValue;
    if (!text || (!text.includes('R') && !text.includes('ZAR'))) return;
    const pattern = /(?:ZAR\s*|R\s*)(\d[\d\s,]*(?:\.\d{1,2})?)(?:\s*ZAR)?/g;
    if (!pattern.test(text)) return;
    pattern.lastIndex = 0;
    originals.set(node, text);
    node.nodeValue = text.replace(pattern, (match, amount) => {
      const value = parseZar(amount);
      return value === null ? match : formatUsd(value);
    });
  }

  function walk(root) {
    if (!root || root.nodeType !== 1) return;
    const candidates = root.matches?.(priceSelector)
      ? [root, ...root.querySelectorAll(priceSelector)]
      : [...root.querySelectorAll(priceSelector)];
    for (const el of candidates) {
      if (el.closest('#orynta-currency-bar, .orynta-estimator')) continue;
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) convertTextNode(node);
    }
  }

  function restore(root = document) {
    const candidates = [...root.querySelectorAll(priceSelector)];
    for (const el of candidates) {
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (originals.has(node)) {
          node.nodeValue = originals.get(node);
          originals.delete(node);
        }
      }
    }
  }

  function applyMode(nextMode) {
    if (applying) return;
    applying = true;
    observer?.disconnect();
    restore();
    mode = nextMode;
    localStorage.setItem(MODE_KEY, mode);
    if (mode === 'USD') walk(document.documentElement);
    document.documentElement.dataset.oryntaCurrency = mode;
    const select = document.querySelector('#orynta-currency-select');
    if (select) select.value = mode;
    const note = document.querySelector('#orynta-currency-estimate');
    if (note) note.hidden = mode !== 'USD';
    observer?.observe(document.body, { childList: true, subtree: true, characterData: true });
    applying = false;
  }

  function addBar() {
    if (document.querySelector('#orynta-currency-bar')) return;
    const bar = document.createElement('div');
    bar.id = 'orynta-currency-bar';
    bar.innerHTML = '<label>Currency <select id="orynta-currency-select" aria-label="Display currency"><option value="USD">USD ($)</option><option value="ZAR">ZAR (R)</option></select></label><span id="orynta-currency-estimate">USD estimates. Final payment in ZAR.</span>';
    document.body.prepend(bar);
    document.querySelector('#orynta-currency-select').addEventListener('change', event => applyMode(event.target.value));
  }

  function addStyles() {
    const style = document.createElement('style');
    style.textContent = '#orynta-currency-bar{display:flex;align-items:center;justify-content:center;gap:18px;padding:8px 20px;background:#050505;color:#fff;font:500 12px/1.35 Arial,sans-serif;letter-spacing:.01em;position:relative;z-index:1000}#orynta-currency-bar label{display:flex;align-items:center;gap:7px;white-space:nowrap}#orynta-currency-select{color:#111;background:#fff;border:1px solid #ddd;border-radius:4px;padding:4px 8px;font:600 12px Arial,sans-serif}@media(max-width:700px){#orynta-currency-bar{align-items:flex-start;justify-content:space-between;text-align:left;font-size:10px;padding:7px 12px;gap:10px}#orynta-currency-bar span{max-width:72%}}';
    document.head.append(style);
  }

  async function detectCountry() {
    const liquidCountry = (document.body.dataset.oryntaCountry || '').toUpperCase();
    try {
      const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true', { credentials: 'same-origin' });
      if (response.ok) {
        const data = await response.json();
        return (data?.detected_values?.country?.handle || liquidCountry || 'ZA').toUpperCase();
      }
    } catch (_) {}
    return liquidCountry || 'ZA';
  }

  async function loadRate() {
    const cached = Number(localStorage.getItem(RATE_KEY));
    const cachedAt = Number(localStorage.getItem(RATE_TIME_KEY));
    if (cached > 0 && Date.now() - cachedAt < 43200000) {
      rate = cached;
      return;
    }
    try {
      const response = await fetch('https://api.frankfurter.app/latest?from=ZAR&to=USD', { signal: AbortSignal.timeout(4000) });
      if (!response.ok) throw new Error('rate');
      const data = await response.json();
      if (data?.rates?.USD > 0) {
        rate = data.rates.USD;
        localStorage.setItem(RATE_KEY, String(rate));
        localStorage.setItem(RATE_TIME_KEY, String(Date.now()));
      }
    } catch (_) {
      rate = RATE_FALLBACK;
    }
  }


  async function init() {
    addStyles();
    addBar();
    await loadRate();
    const saved = localStorage.getItem(MODE_KEY);
    mode = saved === 'USD' || saved === 'ZAR' ? saved : 'USD';
    observer = new MutationObserver(mutations => {
      if (applying || mode !== 'USD') return;
      applying = true;
      observer.disconnect();
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') {
          convertTextNode(mutation.target);
        } else {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1) {
              walk(node);
            } else if (node.nodeType === 3 && node.parentElement?.closest(priceSelector)) {
              convertTextNode(node);
            }
          });
        }
      }
      observer.observe(document.body, { childList: true, subtree: true, characterData: true });
      applying = false;
    });
    applyMode(mode);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();